import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDefaultRoute } from '../utils/getDefaultRoute';
import LoginPage from '../pages/common/LoginPage';
import ErrorPage from '../pages/common/ErrorPage';
import AccessDenied from '../pages/common/AccessDenied';
import Profile from '../pages/common/Profile';
import UnderConstruction from '../pages/common/UnderConstruction';
import AdminLayout from '../layouts/AdminLayout';
import FacultyLayout from '../layouts/FacultyLayout';
import StudentLayout from '../layouts/StudentLayout';
import AdminOverview from '../pages/admin/AdminOverview';
import AdminUsersPage from '../pages/admin/AdminUsersPage';
import AccessControlPage from '../pages/admin/AccessControlPage';
import CodingPlatformsPage from '../pages/admin/CodingPlatformsPage';
import PlagiarismAnalyticsPage from '../pages/common/PlagiarismAnalyticsPage';
import BatchListPage from '../pages/coding-tracker/BatchListPage';
import BatchDetailsPage from '../pages/coding-tracker/BatchDetailsPage';
import AssignmentPage from '../pages/coding-tracker/AssignmentPage';
import AssignmentDetailsPage from '../pages/coding-tracker/AssignmentDetailsPage';
import ProgressPage from '../pages/coding-tracker/ProgressPage';
import BatchStudentDetailsPage from '../pages/coding-tracker/BatchStudentDetailsPage';
import FacultyDashboard from '../pages/faculty/FacultyDashboard';
import FacultyProjectDashboard from '../pages/FacultyProjectDashboard';
import StudentProjectDashboard from '../pages/StudentProjectDashboard';
import MyCourses from '../pages/faculty/MyCourses';
import FacultyLabInternalsPage from '../pages/faculty/FacultyLabInternalsPage';
import CreateLabInternalPage from '../pages/faculty/CreateLabInternalPage';
import FacultyLabDetailPage from '../pages/faculty/FacultyLabDetailPage';
import FacultyStudentEvaluationPage from '../pages/faculty/FacultyStudentEvaluationPage';
import FacultyQuestionBankPage from '../pages/faculty/FacultyQuestionBankPage';
import QuestionFormPage from '../pages/faculty/QuestionFormPage';
import ProjectEvaluation from '../pages/faculty/ProjectEvaluation';
import ProjectDetails from '../pages/common/ProjectDetails';
// import StudentHome from '../pages/student/StudentHome';
import StudentDashboard from '../pages/student/StudentDashboard';
import StudentCodingDashboard from '../pages/student/StudentCodingDashboard';
import StudentProgressPage from '../pages/student/ProgressPage';
import StudentLabDashboard from '../pages/student/StudentLabDashboard';
import StudentLabResultsPage from '../pages/student/StudentLabResultsPage';
import StudentLabResultDetailPage from '../pages/student/StudentLabResultDetailPage';
import StudentSubmissionResultPage from '../pages/student/StudentSubmissionResultPage';
import StudentIDE from '../pages/student/StudentIDE';
import FacultyLabResultsPage from '../pages/faculty/FacultyLabResultsPage';
import FacultyLabSubmissionDetailPage from '../pages/faculty/FacultyLabSubmissionDetailPage';
import CodingOverview from '../pages/faculty/CodingOverview';
import CodingActivityPage from '../pages/faculty/CodingActivityPage';
import {
  RequireAuth,
  RequireFacultyTeaching,
  RequireCbcFaculty,
  ProtectedRoute,
  // RequireStudent,
  LegacyBatchRedirect,
  LegacyProjectDetailRedirect,
  LegacyProjectEvaluateRedirect,
  LegacyCodingTrackerRedirect,
  LegacyProfileRedirect,
  LegacyCoursesPathRedirect,
  LegacyPlagiarismRedirect,
  LegacyCodingOverviewRedirect,
  LegacyCodingActivityRedirect,
  LegacyRoleHomeRedirect,
  RoleBasedProjectRedirect,
} from './routeGuards';
import ProjectUpload from '../pages/student/ProjectUpload';

function DefaultRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultRoute(user.role, user)} replace />;
}

/** Faculty area index: subtype-aware default (see {@link getDefaultRoute}). */
function FacultyIndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={getDefaultRoute(user?.role, user)} replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/unauthorized" element={<AccessDenied />} />

      <Route path="/projects" element={<RoleBasedProjectRedirect />} />

      {/* Legacy / ambiguous URLs → role-scoped routes */}
      <Route path="/dashboard" element={<LegacyRoleHomeRedirect />} />
      <Route path="/profile" element={<LegacyProfileRedirect />} />
      <Route path="/courses/*" element={<LegacyCoursesPathRedirect />} />
      <Route path="/upload" element={<Navigate to="/student/upload" replace />} />
      <Route path="/assignments" element={<Navigate to="/student/assignments" replace />} />
      <Route path="/coding-tracker" element={<LegacyCodingTrackerRedirect />} />
      <Route path="/coding/overview" element={<LegacyCodingOverviewRedirect />} />
      <Route path="/coding/activity" element={<LegacyCodingActivityRedirect />} />
      <Route path="/plagiarism-analytics" element={<LegacyPlagiarismRedirect />} />
      <Route path="/project/:id" element={<LegacyProjectDetailRedirect />} />
      <Route path="/project/:id/evaluate" element={<LegacyProjectEvaluateRedirect />} />
      <Route path="/batch/*" element={<LegacyBatchRedirect />} />

      <Route
        path="/admin"
        element={
          <RequireAuth roles={['admin']}>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<AdminOverview />} />
        <Route path="profile" element={<Profile />} />
        <Route path="courses-overview" element={<UnderConstruction />} />
        <Route path="lab-activity" element={<UnderConstruction />} />
        <Route path="platform-analytics" element={<UnderConstruction />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="access-control" element={<AccessControlPage />} />
        <Route path="coding-platforms" element={<CodingPlatformsPage />} />
        <Route path="plagiarism-analytics" element={<PlagiarismAnalyticsPage />} />
        <Route path="coding-tracker" element={<BatchListPage />} />
        <Route path="batch/:batchId" element={<BatchDetailsPage />} />
        <Route path="batch/:batchId/assignments" element={<AssignmentPage />} />
        <Route path="batch/:batchId/assignments/:assignmentId" element={<AssignmentDetailsPage />} />
        <Route path="batch/:batchId/assignments/:assignmentId/progress" element={<ProgressPage />} />
        <Route path="batch/:batchId/students" element={<BatchStudentDetailsPage />} />
      </Route>

      <Route
        path="/faculty"
        element={
          <RequireAuth roles={['faculty', 'cbcfaculty']}>
            <FacultyLayout />
          </RequireAuth>
        }
      >
        <Route index element={<FacultyIndexRedirect />} />
        <Route path="profile" element={<Profile />} />

        <Route path="dashboard" element={<RequireFacultyTeaching><FacultyDashboard /></RequireFacultyTeaching>} />
        <Route path="grading" element={<RequireFacultyTeaching><FacultyDashboard /></RequireFacultyTeaching>} />
        <Route path="projects" element={<ProtectedRoute allowedRoles={['faculty']}><FacultyProjectDashboard /></ProtectedRoute>} />
        <Route path="projects-dashboard" element={<Navigate to="/faculty/projects" replace />} />
        <Route path="courses" element={<RequireFacultyTeaching><MyCourses /></RequireFacultyTeaching>} />
        <Route path="lab-internals" element={<RequireFacultyTeaching><FacultyLabInternalsPage /></RequireFacultyTeaching>} />
        <Route path="lab-internals/new" element={<RequireFacultyTeaching><CreateLabInternalPage /></RequireFacultyTeaching>} />
        <Route path="lab-internals/:labId/edit" element={<RequireFacultyTeaching><CreateLabInternalPage /></RequireFacultyTeaching>} />
        <Route path="lab-internals/:labId/evaluate" element={<RequireFacultyTeaching><FacultyStudentEvaluationPage /></RequireFacultyTeaching>} />
        <Route
          path="lab-internals/:labId/student/:studentLabId"
          element={<RequireFacultyTeaching><FacultyStudentEvaluationPage /></RequireFacultyTeaching>}
        />
        <Route path="lab-internals/:labId" element={<RequireFacultyTeaching><FacultyLabDetailPage /></RequireFacultyTeaching>} />
        <Route path="questions" element={<RequireFacultyTeaching><FacultyQuestionBankPage /></RequireFacultyTeaching>} />
        <Route path="questions/new" element={<RequireFacultyTeaching><QuestionFormPage /></RequireFacultyTeaching>} />
        <Route path="questions/:id/edit" element={<RequireFacultyTeaching><QuestionFormPage /></RequireFacultyTeaching>} />
        <Route path="project/:id" element={<RequireFacultyTeaching><ProjectDetails /></RequireFacultyTeaching>} />
        <Route path="project/:id/evaluate" element={<RequireFacultyTeaching><ProjectEvaluation /></RequireFacultyTeaching>} />
        <Route path="lab-results" element={<RequireFacultyTeaching><FacultyLabResultsPage /></RequireFacultyTeaching>} />
        <Route
          path="lab-results/:submissionId"
          element={<RequireFacultyTeaching><FacultyLabSubmissionDetailPage /></RequireFacultyTeaching>}
        />

        <Route path="coding-dashboard" element={<RequireCbcFaculty><BatchListPage /></RequireCbcFaculty>} />
        <Route path="coding-tracker" element={<Navigate to="/faculty/coding-dashboard" replace />} />
        <Route path="plagiarism-analytics" element={<RequireCbcFaculty><PlagiarismAnalyticsPage /></RequireCbcFaculty>} />
        <Route path="coding/overview" element={<RequireCbcFaculty><CodingOverview /></RequireCbcFaculty>} />
        <Route path="coding/activity" element={<RequireCbcFaculty><CodingActivityPage /></RequireCbcFaculty>} />
        <Route path="batch/:batchId" element={<RequireCbcFaculty><BatchDetailsPage /></RequireCbcFaculty>} />
        <Route path="batch/:batchId/assignments" element={<RequireCbcFaculty><AssignmentPage /></RequireCbcFaculty>} />
        <Route path="batch/:batchId/assignments/:assignmentId" element={<RequireCbcFaculty><AssignmentDetailsPage /></RequireCbcFaculty>} />
        <Route path="batch/:batchId/assignments/:assignmentId/progress" element={<RequireCbcFaculty><ProgressPage /></RequireCbcFaculty>} />
        <Route path="batch/:batchId/students" element={<RequireCbcFaculty><BatchStudentDetailsPage /></RequireCbcFaculty>} />
      </Route>

      <Route
        path="/student"
        element={
          <RequireAuth roles={['student']}>
            <StudentLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="home" replace />} />
        <Route path="home" element={<StudentDashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="dashboard" element={<StudentDashboard />} />
        {/* <Route path="dashboard/coding" element={<StudentCodingDashboard />} /> */}
        <Route path="projects" element={<ProtectedRoute allowedRoles={['student']}><StudentProjectDashboard /></ProtectedRoute>} />
        <Route path="projects-dashboard" element={<Navigate to="/student/projects" replace />} />
        <Route path="courses" element={<UnderConstruction />} />
        <Route path="assignments" element={<Navigate to="/student/lab-internals" replace />} />
        <Route path='upload' element={<ProtectedRoute allowedRoles={['student']}><ProjectUpload /></ProtectedRoute>} />
        <Route path="lab-internals" element={<StudentLabDashboard />} />
        <Route path="lab-results" element={<StudentLabResultsPage />} />
        <Route path="lab-results/:submissionId" element={<StudentLabResultDetailPage />} />
        <Route path="results/:submissionId" element={<StudentSubmissionResultPage />} />
        <Route path="coding" element={<StudentCodingDashboard />} />
        <Route path="coding/progress" element={<StudentProgressPage />} />
        <Route path="progress" element={<StudentProgressPage />} />
        <Route path="ide/:labInternalId/:questionId" element={<StudentIDE />} />
        <Route path="project/:id" element={<ProjectDetails />} />
      </Route>

      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );  
}
